import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tasksAPI, activitiesAPI, bugsAPI, projectsAPI, usersAPI } from '../utils/api';
import { Badge, Modal, ConfirmDialog } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';
import DateWarnings, { useDateWarnings } from '../components/DateWarnings';
import TimeTracker     from '../components/TimeTracker';
import TaskComments    from '../components/TaskComments';
import AttachmentsPanel from '../components/AttachmentsPanel';
import TagPicker       from '../components/TagPicker';
import { useAuth }     from '../hooks/useAuth';

import { useToast, apiError } from '../components/Toast';
import { useMaster } from '../hooks/useMasters';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { useEditableRecord, SaveBar } from '../components/UnsavedChanges';
import { ArrowLeft, Bug, Calendar, Check, Clock, Layers, Trash2 } from 'lucide-react';
import { STATUS_OPTIONS, statusStyle, isSettled } from '../utils/workStatus';

const TrashIc = () => <Trash2 size={14} />;
const LayersIc = () => <Layers size={14} />;
const ClockIc = () => <Clock size={14} />;
const CalIc = () => <Calendar size={13} />;

const STATUS_OPTS_FALLBACK   = STATUS_OPTIONS;
const PRIORITY_OPTS_FALLBACK = ['Low','Medium','High','Critical'].map(v=>({value:v,label:v}));
const WORK_OPTS_FALLBACK     = ['Implementation','Training','Bug Fix','Change Request','Support'].map(v=>({value:v,label:v}));

const fmt = (val, pattern = 'MMM d, yyyy') => {
  if (!val) return null;
  try { const d = typeof val === 'string' ? parseISO(val) : new Date(val); return isValid(d) ? format(d, pattern) : null; }
  catch { return null; }
};

const InfoRow = ({ label, value, children }) => (
  value || children ? (
    <div className="flex gap-3 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
      <p className="text-xs font-semibold w-28 flex-shrink-0 pt-0.5 uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      <div className="flex-1 text-sm" style={{ color: 'var(--text)' }}>{children || value}</div>
    </div>
  ) : null
);

const EffortBar = ({ estimated, actual }) => {
  const est = parseFloat(estimated)||0, act = parseFloat(actual)||0;
  if (!est && !act) return null;
  const pct = est > 0 ? Math.min(100, Math.round((act/est)*100)) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span style={{ color:'var(--text-secondary)' }}>Effort Progress</span>
        <span className="font-medium" style={{ color: act>est?'#ef4444':'var(--text)' }}>
          {act.toFixed(1)}h actual / {est.toFixed(1)}h estimated
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
        <div className="h-full rounded-full transition-all" style={{ width:`${pct}%`, background:act>est?'#ef4444':'#2E539F' }} />
      </div>
    </div>
  );
};

export default function TaskDetailPage() {
  // Dropdowns come from Masters so an Admin can relabel and reorder them.
  // The _FALLBACK arrays above are used when Masters has nothing configured.
  const STATUS_OPTS = useMaster('task_status', STATUS_OPTS_FALLBACK);
  const PRIORITY_OPTS = useMaster('priority', PRIORITY_OPTS_FALLBACK);
  const WORK_OPTS = useMaster('work_type', WORK_OPTS_FALLBACK);
  const toast = useToast();
  const dateWarn = useDateWarnings();
  const { id } = useParams();
  const navigate = useNavigate();

  const [task,       setTask]       = useState(null);
  const [error, setError] = useState('');
  const [activities, setActivities] = useState([]);
  const [actSummary, setActSummary] = useState(null);
  const [bugs,       setBugs]       = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // One form, no edit mode. See components/UnsavedChanges.jsx.
  const { form, set, discard, dirtyFields, patch } = useEditableRecord(task);

  const [saving,     setSaving]     = useState(false);
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const loadTask = useCallback(() => {
    return Promise.all([
      tasksAPI.getById(id),
      activitiesAPI.getByTask(id),
      bugsAPI.getAll({ task_id: id , limit: 0 }),
    ]).then(([tR, aR, bR]) => {
      setTask(tR.data);
      setActivities(aR.data.activities || []);
      setActSummary(aR.data.summary || null);
      setBugs(Array.isArray(bR.data) ? bR.data : []);
    });
  }, [id]);

  useEffect(() => {
    Promise.all([
      loadTask(),
      projectsAPI.getAll({ limit: 0 }).then(r => setProjects(r.data)),
      usersAPI.getAll().then(r => setUsers(r.data)),
    ]).catch(e => toast.error(apiError(e, 'Could not load this page.'))).finally(() => setLoading(false));
  }, [loadTask]);


  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await tasksAPI.update(id, patch());
      toast.success('Task updated');
      dateWarn.show(saved.data);
      await loadTask();
      // load() refreshes the record and the hook resets the form from it.
    } catch(e) { toast.error(apiError(e, 'Failed to save task. Check your permissions.')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await tasksAPI.delete(id);
    toast.success('Task deleted'); navigate('/tasks'); }
    catch(e) { toast.error(apiError(e, 'Failed to delete task. Check your permissions.')); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!task) return <div className="card text-center py-16"><p style={{ color:'var(--text)' }}>Task not found</p><button onClick={() => navigate('/tasks')} className="btn-secondary mt-3">← Back</button></div>;

  const actDone = activities.filter(a => a.status === 'Completed').length;
  const actTotal = activities.length;
  const actPct  = actTotal > 0 ? Math.round((actDone/actTotal)*100) : 0;

  return (
    <div className="space-y-5">
      <DateWarnings {...dateWarn} />
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color:'var(--text-secondary)' }}>
        <button onClick={() => navigate('/tasks')} className="hover:text-brand-500 flex items-center gap-1"><ArrowLeft size={16}/> Tasks</button>
        {task.project_name && <><span>/</span><Link to={`/projects/${task.project_id}`} className="hover:text-brand-500">{task.project_name}</Link></>}
        <span>/</span><span style={{ color:'var(--text)' }}>{task.title}</span>
      </div>

      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-2" style={{ color:'var(--text)' }}>{task.title}</h1>
            <div className="flex flex-wrap gap-2">
              <Badge value={task.status}   type="status"   />
              <Badge value={task.priority} type="priority" />
              {task.work_type && <span className="badge border border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400">{task.work_type}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => setDeleteOpen(true)} className="btn-danger flex items-center gap-1.5 text-sm"><TrashIc /> Delete</button>
          </div>
        </div>

        {/* Dates row */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="rounded-xl p-3 border" style={{ background:'var(--surface-alt)', borderColor:'var(--border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color:'var(--text-secondary)' }}><CalIc /> Start Date</p>
            <p className="text-base font-bold" style={{ color: task.start_date ? 'var(--text)' : 'var(--text-secondary)' }}>{fmt(task.start_date) || 'Not set'}</p>
          </div>
          <div className="rounded-xl p-3 border" style={{ background:'var(--surface-alt)', borderColor:'var(--border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color:'var(--text-secondary)' }}><CalIc /> Due Date</p>
            <p className="text-base font-bold" style={{ color: task.due_date ? 'var(--text)' : 'var(--text-secondary)' }}>{fmt(task.due_date) || 'Not set'}</p>
          </div>
        </div>

        <EffortBar estimated={task.effort_estimated ?? task.estimated_hours}
          actual={task.effort_actual ?? task.actual_hours} />
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {[
          { key:'overview',     label:'Overview'    },
          { key:'details',      label:'Details'     },
          { key:'activities',   label:'Activities',  count: activities.length },
          { key:'bugs',         label:'Bugs',        count: bugs.length },
          { key:'comments',     label:'Comments'    },
          { key:'attachments',  label:'Attachments' },
          { key:'tags',         label:'Tags'        },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
              ${activeTab === tab.key ? 'border-brand-500 text-brand-500' : 'border-transparent'}`}
            style={activeTab !== tab.key ? { color:'var(--text-secondary)' } : {}}>
            {tab.label}
            {tab.count > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded-full"
                style={{ background: activeTab === tab.key ? '#2E539F' : 'var(--surface-alt)', color: activeTab === tab.key ? 'white' : 'var(--text-secondary)' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Two-column layout */}
      {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column */}
        <div className="space-y-5">
          <div className="card">
            <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Task Info</h3>
            <InfoRow label="Project">{task.project_name
              ? <Link to={`/projects/${task.project_id}`} className="text-brand-500 hover:underline">{task.project_name}</Link>
              : '—'}
            </InfoRow>
            <InfoRow label="Assigned To">{task.assigned_to_name
              ? <div className="flex items-center gap-2"><div className="w-6 h-6 rounded text-xs font-bold text-white flex items-center justify-center" style={{ background:task.assigned_to_color||'#234180' }}>{task.assigned_to_name[0]}</div>{task.assigned_to_name}</div>
              : 'Unassigned'}
            </InfoRow>
            <InfoRow label="Client"       value={task.client} />
            <InfoRow label="Module"       value={task.module} />
            <InfoRow label="Created By"   value={task.created_by_name} />
            <InfoRow label="Created"      value={fmt(task.created_at, 'MMM d, yyyy HH:mm')} />
            <InfoRow label="Updated"      value={fmt(task.updated_at, 'MMM d, yyyy HH:mm')} />
            {task.completed_at && <InfoRow label="Completed" value={fmt(task.completed_at, 'MMM d, yyyy HH:mm')} />}
          </div>

          {bugs.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color:'var(--text)' }}>
                <Bug size={14}/> Linked Bugs ({bugs.length})
              </h3>
              <div className="space-y-2">
                {bugs.map(b => (
                  <div key={b.id} onClick={() => navigate(`/bugs/${b.id}`)}
                    className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-[var(--surface-alt)] transition-colors">
                    <Badge value={b.severity} type="severity" />
                    <p className="text-sm flex-1 truncate" style={{ color:'var(--text)' }}>{b.title}</p>
                    <Badge value={b.status} type="status" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-5">
          {task.description && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Description</h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color:'var(--text)' }}>{task.description}</p>
            </div>
          )}

          {/* Activities */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color:'var(--text)' }}>
                <LayersIc /> Activities
                {actTotal > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-500">{actDone}/{actTotal} done</span>}
              </h3>
              <Link to={`/activities?task_id=${id}`} className="btn-primary text-xs py-1.5 px-3">Manage Activities</Link>
            </div>

            {actTotal > 0 && (
              <div className="mb-4 space-y-1.5">
                <div className="flex justify-between text-xs" style={{ color:'var(--text-secondary)' }}>
                  <span>{actPct}% complete</span>
                  <span>{parseFloat(actSummary?.actual_hours||0).toFixed(1)}h / {parseFloat(actSummary?.estimated_hours||0).toFixed(1)}h</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width:`${actPct}%`, background:actPct===100?'#10b981':'#2E539F' }} />
                </div>
              </div>
            )}

            {activities.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm mb-3" style={{ color:'var(--text-secondary)' }}>No activities yet</p>
                <Link to={`/activities?task_id=${id}`} className="btn-primary text-sm">Add First Activity</Link>
              </div>
            ) : (
              <div className="space-y-2">
                {activities.map(a => (
                  <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor:'var(--border)', opacity:isSettled(a.status)?0.75:1 }}>
                    {/* Hold and Skipped had no mark of their own and read as Pending. */}
                    <span className="flex-shrink-0">{a.status==='Completed'?<Check size={14} color="#10b981" strokeWidth={3}/>:a.status==='In Progress'?<ClockIc/>
                      :<span className="inline-block w-2.5 h-2.5 rounded-full" title={a.status}
                         style={{ background: statusStyle(a.status).dot }}/>}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${a.status==='Completed'?'line-through opacity-60':''}`} style={{ color:'var(--text)' }}>{a.title}</p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {(a.start_date || a.due_date) && (
                          <span className="text-xs flex items-center gap-1" style={{ color:'var(--text-secondary)' }}>
                            <CalIc />{fmt(a.start_date,'MMM d')||'—'} → {fmt(a.due_date,'MMM d')||'—'}
                          </span>
                        )}
                        {a.assigned_to_name && (
                          <span className="text-xs flex items-center gap-1.5" style={{ color:'var(--text-secondary)' }}>
                            <div className="w-4 h-4 rounded text-white font-bold text-xs flex items-center justify-center" style={{ background:a.assigned_to_color||'#2E539F' }}>{a.assigned_to_name[0]}</div>
                      <TimeTracker activityId={a.id}
                        isRunning={!!a.active_timer_started_at}
                        startedAt={a.active_timer_started_at}
                        onChange={loadTask} />
                            {a.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-medium" style={{ color:'var(--text-secondary)' }}>{parseFloat(a.actual_hours||0).toFixed(1)}/{parseFloat(a.estimated_hours||0).toFixed(1)}h</p>
                      <p className="text-xs mt-0.5" style={{ color: statusStyle(a.status).text }}>{a.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>}

      {/* Activity Feed (comments) + Attachments — full width below the detail grid */}
      {task.id && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <TaskComments projectId={task.project_id} taskId={task.id} currentUser={user} />
          <div className="card">
            <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Attachments</h3>
            <AttachmentsPanel entityType="task" entityId={task.id} currentUserId={user?.id} />
          </div>
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color:'var(--text)' }}>
              <LayersIc /> Activities
              {activities.length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-500">{activities.filter(a=>a.status==='Completed').length}/{activities.length} done</span>}
            </h3>
            <Link to={`/activities?task_id=${id}`} className="btn-primary text-xs py-1.5 px-3">Manage Activities</Link>
          </div>
          {activities.length === 0
            ? <div className="text-center py-10"><p className="text-sm mb-3" style={{ color:'var(--text-secondary)' }}>No activities yet</p><Link to={`/activities?task_id=${id}`} className="btn-primary text-sm">Add First Activity</Link></div>
            : <div className="space-y-2">{activities.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-[var(--surface-alt)]" style={{ borderColor:'var(--border)' }}>
                  {/* Hold and Skipped had no mark of their own and read as Pending. */}
                    <span className="flex-shrink-0">{a.status==='Completed'?<Check size={14} color="#10b981" strokeWidth={3}/>:a.status==='In Progress'?<ClockIc/>
                      :<span className="inline-block w-2.5 h-2.5 rounded-full" title={a.status}
                         style={{ background: statusStyle(a.status).dot }}/>}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${a.status==='Completed'?'line-through opacity-60':''}`} style={{ color:'var(--text)' }}>{a.title}</p>
                    <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{a.status} · {a.assigned_to_name||'Unassigned'}</p>
                  </div>
                  <TimeTracker activityId={a.id} isRunning={!!a.active_timer_started_at} startedAt={a.active_timer_started_at} onChange={loadTask}/>
                  <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{parseFloat(a.actual_hours||0).toFixed(1)}/{parseFloat(a.estimated_hours||0).toFixed(1)}h</p>
                </div>
              ))}</div>
          }
        </div>
      )}

      {/* Bugs Tab */}
      {activeTab === 'bugs' && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color:'var(--text)' }}><Bug size={14}/> Linked Bugs ({bugs.length})</h3>
          {bugs.length === 0
            ? <p className="text-sm text-center py-8" style={{ color:'var(--text-secondary)' }}>No bugs linked to this task</p>
            : <div className="space-y-2">{bugs.map(b => (
                <div key={b.id} onClick={() => navigate(`/bugs/${b.id}`)} className="flex items-center gap-2 p-3 rounded-xl border cursor-pointer hover:bg-[var(--surface-alt)]" style={{ borderColor:'var(--border)' }}>
                  <Badge value={b.severity} type="severity"/>
                  <p className="text-sm flex-1 truncate" style={{ color:'var(--text)' }}>{b.title}</p>
                  <Badge value={b.status} type="status"/>
                </div>
              ))}</div>
          }
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && task.id && (
        <TaskComments projectId={task.project_id} taskId={task.id} currentUser={user}/>
      )}

      {/* Attachments Tab */}
      {activeTab === 'attachments' && task.id && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Attachments</h3>
          <AttachmentsPanel entityType="task" entityId={task.id} currentUserId={user?.id}/>
        </div>
      )}

      {/* Tags Tab */}
      {activeTab === 'tags' && task.id && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Tags</h3>
          <TagPicker entityType="task" entityId={task.id}/>
        </div>
      )}

      {/* Details — every editable field in one place. The old modal was missing
          Estimated hours, and offered a Project selector the API ignores, so
          changing it looked like it worked and did nothing. */}
      {activeTab === 'details' && <div className="card space-y-5">
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Details</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="label">Title *</label>
            <input className="input" value={form.title||''} onChange={e=>set({title:e.target.value})}/></div>
          <div className="md:col-span-2"><label className="label">Description</label>
            <textarea className="input" rows={3} value={form.description||''} onChange={e=>set({description:e.target.value})}/></div>

          <div><label className="label">Status</label>
            <select className="select" value={form.status||''} onChange={e=>set({status:e.target.value})}>
              {STATUS_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select></div>
          <div><label className="label">Priority</label>
            <select className="select" value={form.priority||''} onChange={e=>set({priority:e.target.value})}>
              {PRIORITY_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select></div>

          <div><label className="label">Work type</label>
            <select className="select" value={form.work_type||''} onChange={e=>set({work_type:e.target.value})}>
              {WORK_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select></div>
          <div><label className="label">Assigned to</label>
            <select className="select" value={form.assigned_to||''} onChange={e=>set({assigned_to:e.target.value})}>
              <option value="">Unassigned</option>
              {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select></div>

          <div><label className="label">Client</label>
            <input className="input" value={form.client||''} onChange={e=>set({client:e.target.value})}/></div>
          <div><label className="label">Module</label>
            <input className="input" value={form.module||''} onChange={e=>set({module:e.target.value})}/></div>

          <div><label className="label">Start date</label>
            <input type="date" className="input" value={(form.start_date||'').slice(0,10)} onChange={e=>set({start_date:e.target.value})}/></div>
          <div><label className="label">Due date</label>
            <input type="date" className="input" value={(form.due_date||'').slice(0,10)} onChange={e=>set({due_date:e.target.value})}/></div>

          <div><label className="label">Estimated hours</label>
            <input type="number" min="0" step="0.25" className="input"
              value={form.estimated_hours ?? ''} onChange={e=>set({estimated_hours:e.target.value})}/>
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              Used when this task has no activities; otherwise the activities are totalled.
            </p></div>

          <div><label className="label">Project</label>
            <div className="input flex items-center" style={{ background:'var(--surface-alt)', cursor:'default' }}>
              <span style={{ color:'var(--text)' }}>{task.project_name || '—'}</span>
            </div>
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              A task cannot be moved between projects here — its bugs and change
              requests belong to the original project.
            </p></div>
        </div>

        <CustomFieldsEditor appliesTo="task" projectTypeId={task?.project_type_id}
          values={form.custom_fields || {}}
          onChange={v => set({ custom_fields: v })} />
      </div>}

      <SaveBar dirtyFields={dirtyFields} discard={discard} onSave={handleSave} saving={saving} />


      <ConfirmDialog isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={handleDelete}
        title="Delete Task" message={`Delete "${task.title}"? This will also remove all linked activities.`} />
    </div>
  );
}
